import { SystemAttribute } from '../../../../types/attributes';
import { CmsProduct } from '../../../../types/cmsProduct';
import { formatAttributeItemValue } from '../../../../utils/helpers/attrHelpers';

/**
 * Find the product attribute the module was configured to display.
 *
 * Matched by uid, not by display name: the label is localized and editable in
 * Strapi, the uid is not. Same lookup QuickAnswer uses.
 */
const findSharedAttribute = (
  product: CmsProduct,
  sharedProductAttribute?: SystemAttribute,
) => {
  if (!sharedProductAttribute?.uid || !product.attributes?.length) {
    return undefined;
  }

  return product.attributes.find(
    (attribute) => attribute.name?.uid === sharedProductAttribute.uid,
  );
};

/**
 * The spec shown next to the product image, e.g. "Up to 8% Cashback".
 *
 * Returns undefined when the product has no such attribute or its value is a
 * placeholder, so the caller renders nothing rather than a dash.
 */
export const getSharedAttributeValue = (
  product: CmsProduct,
  sharedProductAttribute?: SystemAttribute,
): string | undefined => {
  const attribute = findSharedAttribute(product, sharedProductAttribute);
  if (!attribute) {
    return undefined;
  }

  const value = formatAttributeItemValue(attribute, false, true);

  // `formatAttributeItemValue` is typed `string`, but it does not always return
  // one: its `default` arm passes `attr.value` through (payment type/network,
  // multi-text, Table, RateTable, add-on) and the JSON arm returns
  // `attr.value ?? ''` — every one of those types declares `value` as `any`.
  // An object or array would reach JSX as `{sharedValue}` and throw "Objects
  // are not valid as a React child", taking the whole article render down, so
  // anything but a string reads as "no value here". Same guard, and the same
  // reason, as `BenefitComparisonTable` and `SimilarProducts`.
  if (typeof value !== 'string') {
    return undefined;
  }

  // A number with no value formats as ' - ' (see attrHelpers), so a value made
  // of nothing but dashes and spaces means the product has no value for this
  // attribute — the card shows nothing rather than a dash. Anything else is
  // returned verbatim: the formatter's spacing is part of the value.
  const hasContent = /[^\s-]/.test(value);

  return hasContent ? value : undefined;
};

/**
 * The attribute's "Additional information", shown in a tooltip beside the
 * value. The field only exists on Number / NumberRange catalogue attributes;
 * every other type has no tooltip (CMS-1790, same narrowing as
 * QuickAnswer's `resolveAttributeAdditionalInfo` and ComparisonTable).
 */
export const getSharedAttributeTooltip = (
  product: CmsProduct,
  sharedProductAttribute?: SystemAttribute,
): string | undefined => {
  const attribute = findSharedAttribute(product, sharedProductAttribute);

  switch (attribute?.__typename) {
    case 'ComponentAttributesAttrNumber':
    case 'ComponentAttributesAttrNumberRange':
      return attribute.additional_information?.trim() || undefined;
    default:
      return undefined;
  }
};
